import { useEffect, useMemo, useState } from "react";
import { Button, Form, Header, Icon, Input, Message, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import { useBooking } from "../../context/BookingContext";
import StatusBadge from "./StatusBadge";
import GuestHouseDropdown from "./GuestHouseDropdown";
import DataTable from "./DataTable";
import FrontDeskModal from "./FrontDeskModal";
import LastUpdatedBadge from "./LastUpdatedBadge";
import DepartureCell from "./DepartureCell";
import { bookingDeparture, compareByDeparture, fmt } from "./adminHelpers";

const FRONT_DESK_STATUSES = ["CONFIRMED", "PARTIALLY_CONFIRMED", "CHECKED_IN", "CHECKED_OUT"];
/* Active stays sort to the top by arrival date; departed guests trail
   behind them (also by arrival date) instead of crowding out arrivals. */
const STATUS_SORT_WEIGHT = { CHECKED_OUT: 1 };

const columnHelper = createColumnHelper();

export default function FrontDeskTab({
  guestHouses,
  onOpenBill,
  ghFilter = "All",
  searchQuery = "",
  onFilterChange,
  openBookingId = null,
  onOpenDetail,
  onCloseDetail,
}) {
  const { bookings: allBookings, apiError, apiLoading, lastSyncedAt, reloadFromApi, updateBookingLocal } = useBooking();
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery);

  const bookings = useMemo(
    () => allBookings.filter((b) => FRONT_DESK_STATUSES.includes(b._apiStatus)),
    [allBookings],
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await reloadFromApi();
    } finally {
      setRefreshing(false);
    }
  };

  /* A GH Admin assigned to exactly one guest house has nothing to pick
     between — auto-scope to it and don't offer an "All Guest Houses" choice
     that would just show the same single guest house's data anyway. */
  useEffect(() => {
    if (guestHouses.length === 1 && String(ghFilter) !== String(guestHouses[0].id)) {
      onFilterChange({ gh: guestHouses[0].id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestHouses]);

  /* Commit to the URL a beat behind the keystroke — same debounce pattern as
     BookingsTab's search, added here since this box didn't have one before
     (there was nowhere for rapid-fire writes to go). */
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchQuery) onFilterChange({ fdQuery: searchInput });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  /* Keep the row updated in place (even once fully checked out) rather than
     yanking it from the list — that would rip the modal out from under the
     admin mid-flow, right when they most want to see the confirmation. A
     manual refresh naturally drops it on the next load. */
  const handleUpdated = (mapped) => {
    updateBookingLocal(mapped);
  };

  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return bookings
      .filter((b) => {
        /* A booking's rooms can sit in a house other than the one the indent
           was filed with, so search and the guest-house filter both match on
           either sense — the house holding the room (`stayGuestHouse*`,
           derived server-side) as well as the filed house. The front desk of
           the house the guest walks into means the former. */
        const stayGhIds = (b.stayGuestHouseIds || []).map(Number);
        const stayGhNames = b.stayGuestHouseNames || [];
        if (q && !(
          b.bookingCode?.toLowerCase().includes(q) ||
          (b.applicantName || "").toLowerCase().includes(q) ||
          (b.guestName || "").toLowerCase().includes(q) ||
          (b.guestHouseName || "").toLowerCase().includes(q) ||
          stayGhNames.some((n) => (n || "").toLowerCase().includes(q)) ||
          (b.roomAssignments || []).some((a) => (a.roomNumber || "").toLowerCase().includes(q))
        )) return false;
        if (
          ghFilter !== "All" &&
          Number(b.guestHouseId) !== Number(ghFilter) &&
          !stayGhIds.includes(Number(ghFilter))
        ) return false;
        return true;
      })
      .sort((a, b) => {
        const wa = STATUS_SORT_WEIGHT[a._apiStatus] || 0;
        const wb = STATUS_SORT_WEIGHT[b._apiStatus] || 0;
        if (wa !== wb) return wa - wb;
        /* Most recently approved/checked-in first within each group, same
           as the Booking Summary tab's recency ordering — so a booking that
           just got approved (and still needs check-in) surfaces at the top
           instead of being buried by an earlier arrival date. */
        return (b.updatedAt || "").localeCompare(a.updatedAt || "");
      });
  }, [bookings, searchInput, ghFilter]);

  const filterKey = [searchInput, ghFilter].join("|");
  const activeBooking = openBookingId
    ? bookings.find((b) => String(b.id) === String(openBookingId)) || null
    : null;

  const columns = useMemo(
    () => [
      columnHelper.accessor("bookingCode", {
        header: "Booking ID",
      }),
      columnHelper.accessor("guestName", {
        header: "Guest Name",
        cell: (info) => info.getValue() || "—",
      }),
      /* Where the guest is actually staying — every house holding a room, not
         the house the indent happened to be filed with. Reads the same as the
         Room(s) column beside it. */
      columnHelper.accessor((b) => (b.stayGuestHouseNames || []).join(", "), {
        id: "guestHouseName",
        header: "Guest House",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor(
        (b) =>
          (b.roomAssignments || [])
            .map((a) => a.roomNumber)
            .filter(Boolean)
            .join(", "),
        {
          id: "rooms",
          header: "Room(s)",
          cell: (info) => info.getValue() || "—",
        },
      ),
      columnHelper.accessor("checkIn", {
        header: "Arrival",
        cell: (info) => fmt(info.getValue()),
      }),
      columnHelper.accessor((b) => bookingDeparture(b).key, {
        id: "checkOut",
        header: "Departure",
        cell: (info) => <DepartureCell booking={info.row.original} />,
        sortingFn: (rowA, rowB) => compareByDeparture(rowA.original, rowB.original),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.display({
        id: "actions",
        header: "Action",
        enableSorting: false,
        cell: (info) => {
          const b = info.row.original;
          const isCheckedOut = b._apiStatus === "CHECKED_OUT";
          const isCheckedIn = b._apiStatus === "CHECKED_IN";
          const icon = isCheckedOut ? "eye" : isCheckedIn ? "key" : "sign in";
          const label = isCheckedOut ? "View" : isCheckedIn ? "Manage" : "Check In";
          return (
            <Button
              size="small"
              color={isCheckedOut || isCheckedIn ? undefined : "blue"}
              basic={isCheckedOut || isCheckedIn}
              onClick={() => onOpenDetail(b.id)}
            >
              <Icon name={icon} />
              {label}
            </Button>
          );
        },
      }),
    ],
    [onOpenDetail],
  );

  return (
    <div>
      <Segment basic vertical>
        <Header as="h3" content="Front Desk" subheader="Check in, check out, change departure, or change rooms for approved bookings" />
        <Form>
          <Form.Group>
            {guestHouses.length > 1 && (
              <Form.Field>
                <GuestHouseDropdown
                  value={ghFilter === "All" ? "All" : Number(ghFilter)}
                  options={[
                    { value: "All", label: "All Guest Houses" },
                    ...guestHouses.map((g) => ({ value: g.id, label: g.name })),
                  ]}
                  onChange={(v) => onFilterChange({ gh: v === "All" ? "All" : Number(v) })}
                />
              </Form.Field>
            )}
            <Form.Field width={6}>
              <Input
                icon="search"
                iconPosition="left"
                placeholder="Search by ID, guest, guest house, room…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                action={
                  searchInput
                    ? { icon: "times circle", basic: true, onClick: () => setSearchInput("") }
                    : undefined
                }
              />
            </Form.Field>
            <Form.Field>
              <Button type="button" icon onClick={handleRefresh} loading={refreshing} disabled={refreshing} title="Refresh">
                <Icon name="refresh" />
              </Button>
            </Form.Field>
            <Form.Field>
              <LastUpdatedBadge lastSyncedAt={lastSyncedAt} syncing={apiLoading} />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {apiError && (
        <Message negative>
          <Icon name="exclamation triangle" />
          {apiError}
          <div style={{ marginTop: 12 }}>
            <Button type="button" basic onClick={handleRefresh}>
              <Icon name="refresh" /> Retry
            </Button>
          </div>
        </Message>
      )}
      <DataTable
        key={filterKey}
        data={filtered}
        columns={columns}
        batchSize={25}
        minWidth={1000}
        emptyMessage={
          bookings.length === 0
            ? "No approved bookings awaiting front desk action."
            : "No bookings match your filters."
        }
      />

      {activeBooking && (
        <FrontDeskModal
          booking={activeBooking}
          guestHouses={guestHouses}
          onClose={onCloseDetail}
          onUpdated={handleUpdated}
          onOpenBill={onOpenBill}
        />
      )}
    </div>
  );
}
